import { useState, useEffect, useCallback } from 'react';

import { apiService } from '@/lib/axios';
import { toApprovalsRows } from '@/lib/utils';

/**
 * Check if a nonconformity JSON field has any `check === '1'` entries.
 * Nonconformity fields (specification, qms, regulations, safety) are stored as
 * single objects { check: string, remark: string } (or arrays for backward compat).
 */
function hasNonconformity(field) {
  const items = Array.isArray(field) ? field : field ? [field] : [];
  return items.some((item) => item && item.check === '1');
}

/**
 * Derive the `nc` (non-conformance) flag from all nonconformity fields.
 * Any field with `check === '1'` means the QE is a non-conformance event.
 */
function deriveNc(apiData) {
  return (
    hasNonconformity(apiData.nonconformitySpecification) ||
    hasNonconformity(apiData.nonconformityQms) ||
    hasNonconformity(apiData.nonconformityRegulations) ||
    hasNonconformity(apiData.nonconformitySafety)
  );
}

/**
 * Normalize a Quality Event API response into the frontend form shape.
 *
 * Maps backend field names to frontend form keys expected by the QE components.
 * @param {Object} apiData - The API response data
 * @param {Object} usersMap - Optional map of sub → user object for designation lookup
 */
export const normalizeQuality = (apiData, usersMap = {}) => ({
  // Information tab
  informationDescription: apiData.informationDescription || '',
  informationDetails: apiData.informationDetails || '',
  informationFile: apiData.informationFile || '',
  informationRemark: apiData.informationRemark || '',

  // Nonconformity tab
  nonconformitySpecification: apiData.nonconformitySpecification,
  nonconformityQms: apiData.nonconformityQms,
  nonconformityRegulations: apiData.nonconformityRegulations,
  nonconformitySafety: apiData.nonconformitySafety,
  nonconformityDetails: {
    statement: {
      text: apiData.nonconformityDetails?.statement?.text || '',
      attachments: apiData.nonconformityDetails?.statement?.attachments || [],
      pendingAttachments: [],
    },
    rootCause: {
      text: apiData.nonconformityDetails?.rootCause?.text || '',
    },
  },

  // Derived: is this a non-conformance event?
  nc: deriveNc(apiData),

  // Plan tab
  planChecked: apiData.planChecked,
  planActionReason: apiData.planActionReason,
  planActions: apiData.planActions,
  planRequired: apiData.planRequired,
  planECReason: apiData.planECReason,
  planCriteriaEffectiveness: apiData.planCriteriaEffectiveness,
  planCriteriaQA: apiData.planCriteriaQA,
  planFile: apiData.planFile,

  reviewers: {
    managementApprovers: apiData.reviewers?.managementApprovers?.length
      ? toApprovalsRows(apiData.reviewers.managementApprovers, usersMap)
      : null,
    processOwners: apiData.reviewers?.processOwners?.length
      ? toApprovalsRows(apiData.reviewers.processOwners, usersMap)
      : null,
    qmrQa: apiData.reviewers?.qmrQa?.length
      ? toApprovalsRows(apiData.reviewers.qmrQa, usersMap)
      : null,
  },

  // Summary / metadata
  affectedUnits: apiData.affectedUnits,

  // Action plan & EC completion tracking
  actionPlansCompleted: apiData.actionPlansCompleted,
  actionPlansJustification: apiData.actionPlansJustification,
  ecCompleted: apiData.ecCompleted,
  ecJustification: apiData.ecJustification,

  // Free-form categories (e.g. "Product Quality", "Process Deviation")
  categories: apiData.categories,

  // Lifecycle fields
  id: apiData.id,
  serial: apiData.serial,
  status: apiData.status,
  creator: apiData.creator,
  creatorName: apiData.creatorName,
  reviewerPipeline: apiData.reviewerPipeline,
  currentActorIndex: apiData.currentActorIndex,
  isRejected: apiData.isRejected,
  rejectedBy: apiData.rejectedBy,
  rejectedReason: apiData.rejectedReason,
  rejectedAt: apiData.rejectedAt,
  approvedAt: apiData.approvedAt,
  closedAt: apiData.closedAt,
  linkedDocumentId: apiData.linkedDocumentId,
  linkedDocumentRevision: apiData.linkedDocumentRevision,

  // Soft delete
  deletedAt: apiData.deletedAt,

  // Timestamps
  createdAt: apiData.createdAt,
  updatedAt: apiData.updatedAt,
});

/**
 * Hook to list quality events with optional filters.
 *
 * @param {Object} options
 * @param {string|string[]} options.status - Filter by status (single status or array of statuses)
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {Object} options.usersMap - Optional map of sub → user for designation lookup
 * @returns {{ qualities: Array, loading: boolean, error: string|null, refetch: Function }}
 */
export function useQualities({ status, page = 1, limit = 100, usersMap = {} } = {}) {
  const [qualities, setQualities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadQualities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { page, limit };
      if (status) params.status = status;

      const response = await apiService.qualities.list(params);

      if (response.data.success) {
        const transformed = (response.data.data || []).map((qe) =>
          normalizeQuality(qe, usersMap)
        );
        setQualities(transformed);
      } else {
        setError('Failed to load quality events');
      }
    } catch (err) {
      console.error('Error loading quality events:', err);
      setError('Failed to load quality events');
    } finally {
      setLoading(false);
    }
  }, [status, page, limit]);

  useEffect(() => {
    loadQualities();
  }, [loadQualities]);

  return { qualities, loading, error, refetch: loadQualities };
}

/**
 * Hook to fetch a single quality event by ID.
 *
 * @param {string} id - Quality Event ID (cuid)
 * @param {Object} options
 * @param {Object} options.usersMap - Optional map of sub → user for designation lookup
 * @returns {{ quality: Object|null, form: Object|null, loading: boolean, error: string|null, refetch: Function }}
 */
export function useQuality(id, { usersMap = {} } = {}) {
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (qeId) => {
    if (!qeId) return;
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.qualities.get(qeId);

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.error || 'Failed to load quality event'
        );
      }

      const normalized = normalizeQuality(response.data.data, usersMap);
      setQuality(normalized);

      return normalized;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load quality event';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  // `form` mirrors `quality` for use by form components
  return { quality, form: quality, loading, error, refetch: () => load(id) };
}

/**
 * Mutation hook for submitting a quality event for review.
 *
 * Transitions DRAFT/RETURNED → REVIEWING.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful submit
 * @returns {{ submitMutation: { isLoading, error, mutateAsync } }}
 */
export function useQualitySubmit({ onSuccess } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const submitMutation = useCallback(
    async (id) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.qualities.submit(id);
        if (response.data.success && onSuccess) {
          onSuccess(response.data.data);
        }
        return response.data.data;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to submit quality event';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  return { submitMutation, isLoading, error };
}

/**
 * Mutation hook for approving a quality event in the review pipeline.
 *
 * Transitions REVIEWING → REVIEWING (advance index) or REVIEWING → APPROVED.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful approve
 * @returns {{ approveMutation: { isLoading, error, mutateAsync } }}
 */
export function useQualityApprove({ onSuccess } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const approveMutation = useCallback(
    async (id, body) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.qualities.approve(id, body);
        if (response.data.success && onSuccess) {
          onSuccess(response.data.data);
        }
        return response.data.data;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to approve quality event';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  return { approveMutation, isLoading, error };
}

/**
 * Mutation hook for rejecting a quality event.
 *
 * Transitions REVIEWING → REJECTED.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful reject
 * @returns {{ rejectMutation: { isLoading, error, mutateAsync } }}
 */
export function useQualityReject({ onSuccess } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const rejectMutation = useCallback(
    async (id, body) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.qualities.reject(id, body);
        if (response.data.success && onSuccess) {
          onSuccess(response.data.data);
        }
        return response.data.data;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to reject quality event';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  return { rejectMutation, isLoading, error };
}

/**
 * Mutation hook for returning a quality event to the requestor.
 *
 * Transitions REVIEWING → RETURNED.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful return
 * @returns {{ returnMutation: { isLoading, error, mutateAsync } }}
 */
export function useQualityReturn({ onSuccess } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const returnMutation = useCallback(
    async (id, body) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.qualities.returnQe(id, body);
        if (response.data.success && onSuccess) {
          onSuccess(response.data.data);
        }
        return response.data.data;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to return quality event';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  return { returnMutation, isLoading, error };
}

/**
 * Mutation hook for releasing an approved quality event to the master list.
 *
 * Transitions APPROVED → PENDING_ACTION when action plans exist,
 * or APPROVED → CLOSED when no action plan is needed. QA-only.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful release
 * @returns {{ releaseMutation: { isLoading, error, mutateAsync } }}
 */
export function useQualityRelease({ onSuccess } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const releaseMutation = useCallback(
    async (id) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.qualities.release(id);
        if (response.data.success && onSuccess) {
          onSuccess(response.data.data);
        }
        return response.data.data;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to release quality event';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  return { releaseMutation, isLoading, error };
}

/**
 * Mutation hook for checking action plan completion.
 *
 * PENDING_ACTION → PENDING_EC (if completed=true & EC required), CLOSED (if completed=true & no EC), or CLOSED (if completed=false).
 * QA-only.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful check
 * @returns {{ checkActionPlansMutation: { isLoading, error, mutateAsync } }}
 */
export function useQualityCheckActionPlans({ onSuccess } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkActionPlansMutation = useCallback(
    async (id, body) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.qualities.checkActionPlans(id, body);
        if (response.data.success && onSuccess) {
          onSuccess(response.data.data);
        }
        return response.data.data;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to check action plans';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  return { checkActionPlansMutation, isLoading, error };
}

/**
 * Mutation hook for checking effectiveness check (EC) completion.
 *
 * PENDING_EC → CLOSED (if completed=true or completed=false), since 'No' also closes the QE.
 * QA-only.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - Callback after successful check
 * @returns {{ checkEffectivenessMutation: { isLoading, error, mutateAsync } }}
 */
export function useQualityCheckEffectiveness({ onSuccess } = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkEffectivenessMutation = useCallback(
    async (id, body) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.qualities.checkEffectiveness(
          id,
          body
        );
        if (response.data.success && onSuccess) {
          onSuccess(response.data.data);
        }
        return response.data.data;
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Failed to check effectiveness';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  return { checkEffectivenessMutation, isLoading, error };
}
